import { CalendarPlus, Check, ClipboardList, MessageCircle, Send, Users } from 'lucide-react';
import { Link } from 'react-router';

import { useTranslation } from '@repo/core';

import { BrandMark } from '@/components/brand-mark';
import { Button } from '@/components/ui/button';

import type { LucideIcon } from 'lucide-react';

/**
 * The public front door at `/` — what a signed-out visitor to
 * roster.hitilabs.com sees. Signed-in users never land here; `HomeGate` sends
 * them straight to the app.
 *
 * Static on purpose: no query, no store, nothing that can fail. It is also the
 * page Meta's reviewers open when they check the business behind the WhatsApp
 * number, so it describes the product in plain words rather than selling it.
 *
 * The message preview is drawn from tokens, not a screenshot, so it follows the
 * theme and cannot go stale against an old build of the mobile app.
 */
const STEPS: { key: 'event' | 'send' | 'confirm'; icon: LucideIcon }[] = [
  { key: 'event', icon: CalendarPlus },
  { key: 'send', icon: Send },
  { key: 'confirm', icon: Check },
];

const FEATURES: { key: 'whatsapp' | 'live' | 'report'; icon: LucideIcon }[] = [
  { key: 'whatsapp', icon: MessageCircle },
  { key: 'live', icon: Users },
  { key: 'report', icon: ClipboardList },
];

export function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark className="size-8" />
            <span className="text-base font-extrabold tracking-tight">{t('common.appName')}</span>
          </Link>
          <Button asChild variant="outline" size="sm">
            <Link to="/login">{t('landing.nav.signIn')}</Link>
          </Button>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto grid w-full max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:py-24">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">
              {t('landing.eyebrow')}
            </p>
            <h1 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              {t('landing.title')}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {t('landing.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="shadow-primary">
                <Link to="/login">{t('landing.ctaPrimary')}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#how-it-works">{t('landing.ctaSecondary')}</a>
              </Button>
            </div>
          </div>

          <MessagePreview />
        </section>

        <section id="how-it-works" className="scroll-mt-8 border-y border-border bg-muted/40">
          <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
            <h2 className="text-3xl font-extrabold tracking-tight">{t('landing.howTitle')}</h2>
            <p className="mt-3 text-muted-foreground">{t('landing.howSubtitle')}</p>

            <ol className="mt-10 grid gap-6 md:grid-cols-3">
              {STEPS.map(({ key, icon: Icon }, index) => (
                <li key={key} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <span className="flex size-10 items-center justify-center rounded-lg bg-primary-tint text-primary-tint-foreground">
                      <Icon className="size-5" aria-hidden="true" />
                    </span>
                    <span className="font-mono text-sm text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{t(`landing.steps.${key}.title`)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {t(`landing.steps.${key}.body`)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
          <h2 className="max-w-xl text-3xl font-extrabold tracking-tight">
            {t('landing.featuresTitle')}
          </h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {FEATURES.map(({ key, icon: Icon }) => (
              <div key={key}>
                <Icon className="size-6 text-primary" aria-hidden="true" />
                <h3 className="mt-3 text-lg font-semibold">{t(`landing.features.${key}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {t(`landing.features.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-6 text-sm text-muted-foreground sm:px-6">
          <span className="flex items-center gap-2.5">
            <BrandMark className="size-5" />
            {t('landing.footer', { year: new Date().getFullYear() })}
          </span>
          <Link to="/privacy" className="hover:text-foreground hover:underline">
            {t('landing.footerPrivacy')}
          </Link>
        </div>
      </footer>
    </div>
  );
}

/**
 * A shift request as the staff member sees it, plus the coordinator's side of
 * the same moment. Decorative — the headline already says what it shows — so
 * it is labelled once for assistive tech rather than read line by line.
 */
function MessagePreview() {
  const { t } = useTranslation();

  return (
    <figure
      aria-label={t('landing.preview.label')}
      className="relative mx-auto w-full max-w-sm rounded-3xl border border-border bg-card p-5 shadow-2xl"
    >
      <div aria-hidden="true">
        <div className="rounded-2xl rounded-tl-sm bg-success p-4 text-sm text-foreground">
          <p>{t('landing.preview.greeting')}</p>
          <p className="mt-1 font-semibold">{t('landing.preview.heading')}</p>
          <p className="mt-3 text-muted-foreground">{t('landing.preview.shift')}</p>
          <div className="mt-4 rounded-lg bg-card py-2 text-center font-medium text-primary shadow-xs">
            {t('landing.preview.button')}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-background px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-medium">
            <span className="flex size-6 items-center justify-center rounded-full bg-success text-success-foreground">
              <Check className="size-3.5" />
            </span>
            {t('landing.preview.confirmed')}
          </span>
          <span className="rounded-md bg-primary-tint px-2 py-0.5 font-mono text-xs text-primary-tint-foreground">
            {t('landing.preview.coverage')}
          </span>
        </div>
      </div>
    </figure>
  );
}
